import { NavLogo, SiteIdentityLogo } from "./assets";
import { mustBeSubmenu } from "./helper";
import { BottomNavigationItem, BottomNavigationSubMenu, TopNavigationSubMenu } from "./items";
import {
	Breakpoint,
	DropShadowWeight,
	FontWeight,
	IBottomItem,
	ITopItem,
	ItemsGap,
	NavLogoKind,
	TextSize,
} from "./types";

export interface IDesktopNavBarProps {
	readonly topItems?: ReadonlyArray<ITopItem>;
	readonly bottomItems?: ReadonlyArray<IBottomItem>;
	readonly siteIdentityLinkLogo: NavLogoKind;
	readonly siteIdentityLinkTooltip: string;
	readonly textSize: TextSize;
	readonly mobileBreakpoint: Breakpoint;
	readonly bottomBarItemsGap: ItemsGap;
	readonly itemsFontWeight: FontWeight;
	readonly belowBarDropShadowWeight: DropShadowWeight;
}

const DesktopNavBar: React.FC<IDesktopNavBarProps> = ({
	topItems,
	bottomItems,
	siteIdentityLinkLogo,
	siteIdentityLinkTooltip,
	textSize,
	mobileBreakpoint,
	bottomBarItemsGap,
	itemsFontWeight,
	belowBarDropShadowWeight,
}) => {
	const desktopBottomItems = bottomItems?.filter(({ allowedScreenSizeMode }) => {
		return (
			allowedScreenSizeMode === "desktop" ||
			allowedScreenSizeMode === "any" ||
			allowedScreenSizeMode === undefined
		);
	});
	let contentBelowTopPadding = "";
	if (topItems && topItems.length > 0) {
		if (desktopBottomItems && desktopBottomItems.length > 0) {
			contentBelowTopPadding = "mt-[102px]";
		} else {
			contentBelowTopPadding = "mt-[36px]";
		}
	} else if (desktopBottomItems && desktopBottomItems.length > 0) {
		contentBelowTopPadding = "mt-[66px]";
	} else {
		contentBelowTopPadding = "mt-[0px]";
	}

	const blockAtBreakpoint =
		mobileBreakpoint === Breakpoint.xl
			? "xl:block"
			: mobileBreakpoint === Breakpoint.lg
			? "lg:block"
			: "md:block";

	return (
		<>
			<nav className={`fixed z-10 hidden w-full bg-white ${blockAtBreakpoint}`} id="navbar_desktop">
				{topItems && topItems.length > 0 ? (
					<div
						className={`relative z-30 bg-[color:var(--epic-nav-top-bar-background-color)] mb-px ${
							belowBarDropShadowWeight === DropShadowWeight.Heavy
								? "drop-shadow-[0_4px_10px_rgba(0,0,0,0.25)]"
								: "drop-shadow-[0_4px_3px_rgba(0,0,0,0.25)]"
						}`}
					>
						<div className="max-w-screen-xl mx-auto">
							<ul className="flex flex-row items-stretch justify-center gap-4 h-9">
								{topItems
									? topItems.map(topItem => {
											if (topItem.subItems) {
												return (
													<TopNavigationSubMenu
														item={topItem}
														key={`nav_bar_top_navigation_submenu_${topItem.navLogoKind}`}
													/>
												);
											} else if (!mustBeSubmenu(topItem.navLogoKind)) {
												return (
													<NavLogo
														key={`nav_bar_full_logo_${topItem.navLogoKind}`}
														navLogoKind={topItem.navLogoKind}
														selected={topItem.selected}
														tooltip={topItem.tooltip}
													/>
												);
											}
									  })
									: null}
							</ul>
						</div>
					</div>
				) : null}
				{desktopBottomItems && desktopBottomItems.length > 0 ? (
					<div
						className={`relative z-20 font-title ${
							itemsFontWeight === FontWeight.extraBold ? "font-extrabold" : "font-semibold"
						} bg-[color:var(--epic-nav-bottom-bar-back-color)] ${
							belowBarDropShadowWeight === DropShadowWeight.Heavy
								? "drop-shadow-[0_4px_10px_rgba(0,0,0,0.25)]"
								: "drop-shadow-[0_4px_3px_rgba(0,0,0,0.25)]"
						} ${
							textSize === TextSize.Large
								? `text-sm ${
										mobileBreakpoint === Breakpoint.xl
											? "xl:text-xl"
											: mobileBreakpoint === Breakpoint.lg
											? "lg:text-xl"
											: "md:text-xl"
								  }`
								: textSize === TextSize.Medium
								? `text-xs ${
										mobileBreakpoint === Breakpoint.xl
											? "xl:text-lg"
											: mobileBreakpoint === Breakpoint.lg
											? "lg:text-lg"
											: "md:text-lg"
								  }`
								: `text-xs ${
										mobileBreakpoint === Breakpoint.xl
											? "xl:text-sm"
											: mobileBreakpoint === Breakpoint.lg
											? "lg:text-sm"
											: "md:text-sm"
								  }`
						}`}
					>
						<div className="max-w-screen-xl mx-auto">
							<div
								className={`flex items-center justify-between h-16 ${
									bottomBarItemsGap === ItemsGap.Large
										? "gap-6"
										: bottomBarItemsGap === ItemsGap.Medium
										? "gap-4"
										: "gap-2"
								} px-8`}
							>
								{siteIdentityLinkLogo ? (
									<SiteIdentityLogo
										navLogoKind={siteIdentityLinkLogo}
										tooltip={siteIdentityLinkTooltip}
									/>
								) : null}
								<ul
									role="menubar"
									className={`flex flex-row items-center justify-center h-full ${
										bottomBarItemsGap === ItemsGap.Large
											? "gap-16"
											: bottomBarItemsGap === ItemsGap.Medium
											? "gap-6"
											: "gap-2"
									}`}
								>
									{desktopBottomItems
										? desktopBottomItems.map(bottomItem => {
												if (bottomItem.subItems) {
													return (
														<BottomNavigationSubMenu
															item={bottomItem}
															key={`nav_bar_bottom_navigation_submenu_${bottomItem.label}`}
															highlightForOpenPage={bottomItem.allowhighlight !== false}
														/>
													);
												} else if (bottomItem.href) {
													return (
														<BottomNavigationItem
															item={bottomItem}
															key={`nav_bar_bottom_navigation_item_${bottomItem.label}`}
															highlightForOpenPage={bottomItem.allowhighlight !== false}
														/>
													);
												}
										  })
										: null}
								</ul>
							</div>
						</div>
					</div>
				) : null}
			</nav>
			<div className={`${contentBelowTopPadding} hidden  ${blockAtBreakpoint}`}></div>
		</>
	);
};
export default DesktopNavBar;

